import { useEffect, ComponentType, ReactNode } from 'react'
import styled from 'styled-components'
import { useTrackVisibility } from 'react-intersection-observer-hook'

import PlaceholderCard from '../../cards/PlaceholderCard'

const Viewport = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  --gutter: var(--paginated-slides-gutter, var(--ds-space-04));
  scroll-padding-inline: var(--gutter);
  margin-inline: calc(-1 * var(--gutter));
  padding-inline: var(--gutter);
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const Track = styled.div`
  display: flex;
  gap: var(--ds-space-04);
`

const Item = styled.div`
  flex: 0 0 46%;
  min-width: 0;
  scroll-snap-align: start;
`

type SnapListProps<T> = {
  items: T[]
  ItemComponent: ComponentType<{ item: T }>
  loading: boolean
  canFetchMore: boolean
  onFetchMore: (...args: any) => any
  loadingPlaceholder: ReactNode
}

const SnapList = <T extends Record<'id', string>>({
  loading,
  canFetchMore,
  onFetchMore,
  items,
  ItemComponent,
  loadingPlaceholder = <PlaceholderCard portrait={false} />,
}: SnapListProps<T>) => {
  const [loaderRef, { isVisible }] = useTrackVisibility()

  useEffect(() => {
    if (isVisible && canFetchMore && !loading) {
      onFetchMore()
    }
  }, [isVisible, canFetchMore, loading, onFetchMore])

  return (
    <Viewport>
      <Track>
        {items.map((item) => (
          <Item key={item.id}>
            <ItemComponent item={item} />
          </Item>
        ))}

        {canFetchMore && <Item ref={loaderRef}>{loadingPlaceholder}</Item>}
      </Track>
    </Viewport>
  )
}

export default SnapList
